import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Plus, Trash2, Folder, ArrowUpRight, Search, LayoutGrid, List,
  Factory, Building2, Mountain, Layers, FileText, FlaskConical,
  Activity, Calendar, MapPin, Sparkles, Hash, Inbox, Shield, Crown,
} from "lucide-react";
import api, { formatErr } from "../lib/api";
import AppShell from "../components/AppShell";
import ProjectWizardModal from "../components/ProjectWizardModal";
import AIGreeter from "../components/AIGreeter";
import TrialBanner, { TRIAL_EXPIRED_MSG } from "../components/TrialBanner";
import AdminModule from "../components/AdminModule";
import { useAuth } from "../lib/auth";
import { getProjectIndustryLabel } from "../lib/industryLabel";

const DOMAIN_META = {
  process_plant: { label: "Process Industry", color: "#F26B1F", icon: Factory },
};

/**
 * Resolve the most specific, human-readable industry label for a project.
 * Delegated to the shared util so the Workspace header + Stage rail can
 * surface the same string.
 */

const DELIVERABLE_META = [
  { id: "design_runs", label: "Design Runs", icon: Layers, color: "#7AB8FF",
    domains: ["process_plant", "infrastructure", "real_estate"] },
  { id: "process_simulations", label: "Process Sims", icon: FlaskConical, color: "#F26B1F",
    domains: ["process_plant"] },
  { id: "infra_simulations", label: "Infra Sims", icon: Mountain, color: "#34A853",
    domains: ["infrastructure"] },
  { id: "re_simulations", label: "Real-Estate Sims", icon: Building2, color: "#FBBC04",
    domains: ["real_estate"] },
  { id: "boq_runs", label: "BOQ Envelopes", icon: FileText, color: "#E14B4B",
    domains: ["process_plant", "infrastructure", "real_estate"] },
  { id: "geotech_reports", label: "Geotech Reports", icon: Mountain, color: "#9C7BFB",
    domains: ["infrastructure", "real_estate"] },
  { id: "green_assessments", label: "Green Scorecards", icon: Sparkles, color: "#34A853",
    domains: ["real_estate", "infrastructure"] },
  { id: "compliance_checks", label: "Compliance Runs", icon: Activity, color: "#FF8C42",
    domains: ["process_plant", "infrastructure", "real_estate"] },
  { id: "bim_plans", label: "BIM Plans", icon: Layers, color: "#7AB8FF",
    domains: ["infrastructure", "real_estate"] },
  { id: "foundation_designs", label: "Foundations", icon: Mountain, color: "#FBBC04",
    domains: ["infrastructure", "real_estate"] },
];

export default function Projects() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [scope, setScope] = useState(null); // {unrestricted, domains, sectors}
  const [showAdmin, setShowAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");
  const [filterDomain, setFilterDomain] = useState("all");
  const [view, setView] = useState("grid");
  const [sortKey, setSortKey] = useState("updated_desc");
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: projects }, { data: kpiData }, scopeRes] = await Promise.all([
        api.get("/projects"),
        api.get("/dashboard/kpis").catch(() => ({ data: null })),
        api.get("/auth/access-scope").catch(() => ({ data: null })),
      ]);
      setItems(projects);
      setKpis(kpiData);
      setScope(scopeRes?.data || null);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Lightweight heartbeat — pings every 60 s while the dashboard is
  // open so the super-admin analytics capture true session length.
  useEffect(() => {
    const ping = () => api.post("/auth/heartbeat").catch(() => {});
    ping();
    const id = setInterval(ping, 60_000);
    return () => clearInterval(id);
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this project and all its runs?")) return;
    try {
      await api.delete(`/projects/${id}`);
      setItems(items.filter((p) => p.id !== id));
      // Refresh KPIs
      api.get("/dashboard/kpis").then(({ data }) => setKpis(data)).catch(() => {});
    } catch (e) { alert(formatErr(e.response?.data?.detail) || e.message); }
  };

  const filtered = useMemo(() => {
    let list = items;
    if (filterDomain !== "all") list = list.filter((p) => p.domain === filterDomain);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((p) =>
        (p.name || "").toLowerCase().includes(q) ||
        (p.industry || "").toLowerCase().includes(q) ||
        (p.sub_type || "").toLowerCase().includes(q) ||
        getProjectIndustryLabel(p).toLowerCase().includes(q) ||
        (p.country || "").toLowerCase().includes(q));
    }
    if (sortKey === "name_asc") list = [...list].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    else if (sortKey === "created_desc") list = [...list].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    else list = [...list].sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
    return list;
  }, [items, search, filterDomain, sortKey]);

  const trialExpired = !!(user?.trial && user.trial.expired);
  const isAdminLike = ["admin", "super_admin"].includes((user?.role || "").toLowerCase());
  const isSuper = (user?.role || "").toLowerCase() === "super_admin";

  // Filter the deliverable tiles to the user's allowed domains so a
  // Process-Plant-only tenant never sees Infra / Real-Estate counts.
  const allowedDeliverables = useMemo(() => {
    if (!scope || scope.unrestricted) return DELIVERABLE_META;
    const allowed = new Set(scope.domains || []);
    return DELIVERABLE_META.filter((d) =>
      d.domains.some((dom) => allowed.has(dom)));
  }, [scope]);

  return (
    <AppShell>
      <AIGreeter />
      <div style={{ maxWidth: "min(96vw, 1920px)", width: "100%", margin: "0 auto", padding: "28px clamp(20px, 3vw, 48px) 80px" }}>

        <TrialBanner />

        {/* Heading + CTA */}
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 20, marginBottom: 24, flexWrap: "wrap" }}>
          <div>
            <div className="pf-overline" style={{ marginBottom: 6 }}>WORKBENCH · DASHBOARD</div>
            <h1 className="pf-heading" data-testid="dashboard-title"
                style={{ fontSize: 34, margin: 0, letterSpacing: "-0.03em" }}>
              Welcome back
            </h1>
            <p style={{ color: "var(--pf-text-muted)", marginTop: 6 }}>
              Live KPIs · classification across domains · jump back into any workbench.
            </p>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {isSuper && (
              <button className="btn btn-ghost"
                      onClick={() => nav("/superadmin")}
                      data-testid="dashboard-superadmin-btn"
                      style={{ display: "inline-flex", alignItems: "center", gap: 6,
                                borderColor: "var(--pf-spark)",
                                color: "var(--pf-spark)" }}
                      title="Platform-wide usage analytics">
                <Crown size={14}/> Super-Admin
              </button>
            )}
            {isAdminLike && (
              <button className="btn btn-ghost"
                      onClick={() => setShowAdmin(true)}
                      data-testid="dashboard-admin-btn"
                      style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                      title="Manage users, roles and project access">
                <Shield size={14}/> Admin
              </button>
            )}
            <button className="btn btn-ghost" onClick={() => nav("/reviewer/inbox")}
                    data-testid="dashboard-reviewer-inbox-btn"
                    style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                    title="Cross-project audit triage">
              <Inbox size={14}/> Reviewer Inbox
            </button>
            <button className="btn btn-primary"
                    onClick={() => {
                      if (trialExpired) { alert(TRIAL_EXPIRED_MSG); return; }
                      setCreating(true);
                    }}
                    disabled={trialExpired}
                    title={trialExpired ? TRIAL_EXPIRED_MSG : "Create a new project"}
                    data-testid="dashboard-new-btn">
              <Plus size={14}/> New project
            </button>
          </div>
        </div>

        {err && <div className="pf-sev pf-sev-error" data-testid="dashboard-error"
                     style={{ display: "block", padding: "10px 12px", marginBottom: 16 }}>{err}</div>}

        <ProjectWizardModal open={creating}
          onClose={() => setCreating(false)}
          onCreated={(p) => p && nav(`/projects/${p.id}`)} />

        {showAdmin && (
          <AdminModule allProjects={items}
                        onUsersChange={load}
                        onClose={() => setShowAdmin(false)} />
        )}

        {/* ── KPI HERO ROW ───────────────────────────────────────────── */}
        {kpis && (
          <div data-testid="kpi-hero" style={{
            display: "grid",
            gridTemplateColumns: `repeat(${1 + Object.entries(DOMAIN_META).filter(([k]) => !scope || scope.unrestricted || (scope.domains||[]).includes(k)).length}, minmax(0, 1fr))`,
            gap: 12, marginBottom: 18,
          }}>
            <KpiCard title="TOTAL PROJECTS" value={kpis.total_projects} accent="#F26B1F"
                      icon={Folder}
                      sub={`+${kpis.growth.last_7_days} new this week`}
                      testid="kpi-total" big />
            {Object.entries(DOMAIN_META)
              .filter(([key]) =>
                !scope || scope.unrestricted
                  || (scope.domains || []).includes(key))
              .map(([key, meta]) => {
              const Icon = meta.icon;
              const count = kpis.by_domain[key] || 0;
              const pct = kpis.total_projects > 0
                ? Math.round((count / kpis.total_projects) * 100) : 0;
              return (
                <KpiCard key={key} title={meta.label.toUpperCase()}
                          value={count} accent={meta.color}
                          icon={Icon}
                          sub={`${pct}% of portfolio`}
                          testid={`kpi-domain-${key}`} />
              );
            })}
          </div>
        )}

        {/* ── DELIVERABLES STRIP ─────────────────────────────────────── */}
        {kpis && (
          <div data-testid="kpi-deliverables" style={{
            border: "1px solid var(--pf-border)",
            background: "var(--pf-surface)",
            borderRadius: 14, padding: 16, marginBottom: 18,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <Hash size={14} style={{ color: "#F26B1F" }} />
              <span className="pf-overline">DELIVERABLES GENERATED · {kpis.deliverables.total_artefacts} TOTAL</span>
            </div>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
              gap: 10,
            }}>
              {allowedDeliverables.map((d) => {
                const v = kpis.deliverables[d.id] || 0;
                const Icon = d.icon;
                return (
                  <div key={d.id} data-testid={`deliv-${d.id}`}
                        style={{
                          padding: "10px 12px", borderRadius: 8,
                          background: "var(--pf-surface-2)",
                          border: "1px solid var(--pf-border)",
                          display: "flex", alignItems: "center", gap: 10,
                        }}>
                    <Icon size={16} style={{ color: d.color, flexShrink: 0 }} />
                    <div style={{ overflow: "hidden" }}>
                      <div style={{ fontSize: 18, fontWeight: 700, color: "var(--pf-fg)" }}>{v}</div>
                      <div style={{ fontSize: 10, color: "var(--pf-text-muted)",
                                      whiteSpace: "nowrap", overflow: "hidden",
                                      textOverflow: "ellipsis" }}>{d.label}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── DISTRIBUTION + TIMELINE ROW ────────────────────────────── */}
        {kpis && (
          <div style={{
            display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12,
            marginBottom: 18,
          }}>
            <DistributionCard title="By Industry" data={kpis.by_industry}
              testid="dist-industry" />
            <DistributionCard title="By Country / Region" data={kpis.by_country}
              testid="dist-country" icon={MapPin} />
          </div>
        )}

        {/* ── PROJECT LIST CONTROLS ──────────────────────────────────── */}
        <div data-testid="project-controls" style={{
          display: "flex", gap: 10, marginBottom: 14, alignItems: "center",
          flexWrap: "wrap",
        }}>
          <h2 className="pf-heading" style={{ fontSize: 18, margin: 0, marginRight: 6 }}>
            Your projects {filtered.length !== items.length && (
              <span className="pf-muted" style={{ fontSize: 13, fontWeight: 400 }}>
                ({filtered.length} of {items.length})
              </span>
            )}
          </h2>

          <div style={{ flex: 1, position: "relative", minWidth: 200 }}>
            <Search size={14} style={{
              position: "absolute", left: 10, top: "50%",
              transform: "translateY(-50%)", color: "var(--pf-text-muted)",
            }} />
            <input data-testid="proj-search" placeholder="Search by name, industry, country…"
              value={search} onChange={(e) => setSearch(e.target.value)}
              style={{
                width: "100%", padding: "7px 10px 7px 32px",
                fontSize: 13, borderRadius: 6,
                background: "var(--pf-surface-2)", color: "var(--pf-fg)",
                border: "1px solid var(--pf-border)",
              }} />
          </div>

          <select data-testid="proj-filter-domain" value={filterDomain}
            onChange={(e) => setFilterDomain(e.target.value)} style={selStyle}>
            <option value="all">All domains</option>
            {Object.entries(DOMAIN_META).map(([k, m]) =>
              <option key={k} value={k}>{m.label}</option>)}
          </select>

          <select data-testid="proj-sort" value={sortKey}
            onChange={(e) => setSortKey(e.target.value)} style={selStyle}>
            <option value="updated_desc">Updated · newest</option>
            <option value="created_desc">Created · newest</option>
            <option value="name_asc">Name · A-Z</option>
          </select>

          <div style={{ display: "flex", border: "1px solid var(--pf-border)",
                         borderRadius: 6, overflow: "hidden" }}>
            <ViewBtn active={view === "grid"} onClick={() => setView("grid")}
              testid="view-grid"><LayoutGrid size={14} /></ViewBtn>
            <ViewBtn active={view === "list"} onClick={() => setView("list")}
              testid="view-list"><List size={14} /></ViewBtn>
          </div>
        </div>

        {loading ? (
          <div className="pf-card" style={{ padding: 28, textAlign: "center", color: "var(--pf-text-muted)" }}>Loading…</div>
        ) : items.length === 0 ? (
          <EmptyState onNew={() => setCreating(true)} />
        ) : view === "grid" ? (
          <ProjectGrid items={filtered} onRemove={remove} />
        ) : (
          <ProjectListRows items={filtered} onRemove={remove} />
        )}
      </div>
    </AppShell>
  );
}

/* ─────────────────────────────── KPI Card ────────────────────────── */
function KpiCard({ title, value, sub, accent, icon: Icon, testid, big }) {
  return (
    <div data-testid={testid} style={{
      padding: big ? "20px 22px" : "16px",
      border: "1px solid var(--pf-border)",
      background: "var(--pf-surface)", borderRadius: 14,
      position: "relative", overflow: "hidden",
    }}>
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 3,
        background: accent,
      }} />
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <Icon size={14} style={{ color: accent }} />
        <span className="pf-overline" style={{ fontSize: 10 }}>{title}</span>
      </div>
      <div style={{
        fontSize: big ? 38 : 28, fontWeight: 700, lineHeight: 1,
        color: "var(--pf-fg)", letterSpacing: "-0.02em",
      }}>{value}</div>
      {sub && (
        <div style={{
          fontSize: 11, color: "var(--pf-text-muted)", marginTop: 6,
        }}>{sub}</div>
      )}
    </div>
  );
}

/* ─────────────────────── Distribution bar card ───────────────────── */
function DistributionCard({ title, data, testid, icon: Icon = Activity }) {
  const entries = Object.entries(data || {});
  const max = Math.max(1, ...entries.map(([, v]) => v));
  return (
    <div data-testid={testid} style={{
      border: "1px solid var(--pf-border)",
      background: "var(--pf-surface)", borderRadius: 14, padding: 16,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <Icon size={14} style={{ color: "#F26B1F" }} />
        <span className="pf-overline">{title.toUpperCase()}</span>
      </div>
      {entries.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--pf-text-muted)" }}>No data yet.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {entries.map(([k, v]) => (
            <div key={k} style={{
              display: "grid", gridTemplateColumns: "150px 1fr 36px",
              alignItems: "center", gap: 10, fontSize: 12,
            }}>
              <span style={{ color: "var(--pf-fg)",
                              whiteSpace: "nowrap", overflow: "hidden",
                              textOverflow: "ellipsis" }}>{k}</span>
              <div style={{ height: 8, background: "var(--pf-surface-2)",
                             borderRadius: 4, overflow: "hidden" }}>
                <div style={{
                  width: `${(v / max) * 100}%`, height: "100%",
                  background: "linear-gradient(90deg,#F26B1F,#FBBC04)",
                  borderRadius: 4, transition: "width .35s ease",
                }} />
              </div>
              <span className="pf-mono" style={{
                fontSize: 11, color: "var(--pf-text-muted)", textAlign: "right",
              }}>{v}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ───────────────────────── Project Grid ──────────────────────────── */
function ProjectGrid({ items, onRemove }) {
  return (
    <div style={{
      display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
      gap: 14,
    }}>
      {items.map((p) => <ProjectCard key={p.id} p={p} onRemove={onRemove} />)}
    </div>
  );
}

function ProjectCard({ p, onRemove }) {
  const meta = DOMAIN_META[p.domain] || DOMAIN_META.process_plant;
  const Icon = meta.icon;
  const bimStale = !!(p.bim_pipeline && p.bim_pipeline.stale);
  const hasBIM = !!(p.bim_pipeline && (
    p.bim_pipeline?.blender?.glb_url ||
    p.bim_pipeline?.aps?.urn
  ));
  const [regenBusy, setRegenBusy] = useState(false);
  const regenBIM = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setRegenBusy(true);
    try {
      await api.post(`/projects/${p.id}/bim/generate`);
      // Optimistic: clear local stale flag; full reload will happen on next nav
      p.bim_pipeline = { ...(p.bim_pipeline || {}), stale: false };
    } catch (err) {
      alert("BIM regenerate failed: "
        + (err?.response?.data?.detail || err.message));
    } finally {
      setRegenBusy(false);
    }
  };
  return (
    <div className="pf-card" data-testid={`project-card-${p.id}`}
          style={{
            padding: 18, display: "flex", flexDirection: "column",
            justifyContent: "space-between", minHeight: 170,
            transition: "border-color .2s ease, transform .15s ease",
            borderLeft: `3px solid ${meta.color}`,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--pf-accent)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--pf-border)"; e.currentTarget.style.borderLeftColor = meta.color; e.currentTarget.style.transform = "none"; }}>
      <div>
        <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
          <span className="pf-ind-chip"
                 data-testid={`project-domain-${p.id}`}
                 style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Icon size={11} /> {meta.label}
          </span>
          <span className="pf-ind-chip" data-testid={`project-industry-${p.id}`}
                 style={{ borderColor: "var(--pf-spark)", color: "var(--pf-spark)" }}
                 title={p.sub_type || p.industry || ""}>
            {getProjectIndustryLabel(p)}
          </span>
          {p.country && <span className="pf-ind-chip" data-testid={`project-country-${p.id}`}>{p.country}</span>}
          {bimStale && (
            <span data-testid={`project-bim-stale-${p.id}`}
                   title={p.bim_pipeline?.stale_reason
                     || "Project has changed since the BIM model was generated"}
                   style={{ fontSize: 10, fontWeight: 700,
                            padding: "2px 7px", borderRadius: 999,
                            background: "rgba(245,158,11,0.18)",
                            border: "1px solid #d97706",
                            color: "#fbbf24",
                            display: "inline-flex", alignItems: "center",
                            gap: 4, letterSpacing: 0.4 }}>
              ⚠ BIM out of date
            </span>
          )}
        </div>
        <div className="pf-heading" data-testid={`project-name-${p.id}`}
              style={{ fontSize: 18, margin: 0, lineHeight: 1.3 }}>{p.name}</div>
        {p.description && (
          <div style={{
            color: "var(--pf-text-muted)", marginTop: 6, fontSize: 13,
            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}>{p.description}</div>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 16, gap: 8, flexWrap: "wrap" }}>
        <div className="pf-mono" style={{ fontSize: 10, color: "var(--pf-text-dim)",
                                              display: "flex", alignItems: "center", gap: 4 }}>
          <Calendar size={9} /> {new Date(p.updated_at).toLocaleDateString()}
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "nowrap" }}>
          {bimStale && (
            <button className="btn btn-ghost"
                     onClick={regenBIM}
                     disabled={regenBusy}
                     data-testid={`project-bim-regen-${p.id}`}
                     title="Regenerate BIM photorealistic model with the latest project state"
                     style={{ color: "#fbbf24", borderColor: "#d97706",
                              fontWeight: 600,
                              cursor: regenBusy ? "wait" : "pointer" }}>
              {regenBusy ? "⟳ Regenerating…" : "⟳ Regenerate BIM"}
            </button>
          )}
          {hasBIM && !bimStale && (
            <span data-testid={`project-bim-fresh-${p.id}`}
                   title="BIM model is in sync with current project state"
                   style={{ fontSize: 10, color: "#34d399",
                            padding: "4px 8px", border: "1px solid #064e3b",
                            borderRadius: 4, alignSelf: "center",
                            fontWeight: 600, letterSpacing: 0.4 }}>
              ◧ BIM ready
            </span>
          )}
          <Link to={`/projects/${p.id}/dashboard`} className="btn btn-ghost"
                 data-testid={`project-dashboard-${p.id}`}>Dashboard</Link>
          <Link to={`/projects/${p.id}`} className="btn btn-ghost"
                 data-testid={`project-open-${p.id}`}>Open <ArrowUpRight size={14}/></Link>
          <button className="btn btn-ghost"
                   onClick={() => onRemove(p.id)}
                   title="Delete this project"
                   data-testid={`project-delete-${p.id}`}
                   style={{ color: "var(--pf-err)",
                            display: "inline-flex", alignItems: "center" }}>
            <Trash2 size={14}/>
          </button>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── Project List ──────────────────────────── */
function ProjectListRows({ items, onRemove }) {
  return (
    <div data-testid="project-list" style={{
      border: "1px solid var(--pf-border)", borderRadius: 10,
      overflow: "hidden", background: "var(--pf-surface)",
    }}>
      <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ background: "var(--pf-surface-2)" }}>
            <th style={th}>Name</th>
            <th style={th}>Domain</th>
            <th style={th}>Industry</th>
            <th style={th}>Country</th>
            <th style={th}>Updated</th>
            <th style={{ ...th, textAlign: "right" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((p) => {
            const meta = DOMAIN_META[p.domain] || DOMAIN_META.process_plant;
            const Icon = meta.icon;
            return (
              <tr key={p.id} data-testid={`project-row-${p.id}`}
                   style={{ borderTop: "1px solid var(--pf-border)" }}>
                <td style={{ ...td, fontWeight: 600 }}>
                  {p.name}
                  {p.bim_pipeline?.stale && (
                    <span data-testid={`project-bim-stale-${p.id}`}
                           title={p.bim_pipeline?.stale_reason
                             || "Project has changed since BIM generation"}
                           style={{ marginLeft: 8, fontSize: 9, fontWeight: 700,
                                    padding: "1px 6px", borderRadius: 999,
                                    background: "rgba(245,158,11,0.18)",
                                    border: "1px solid #d97706",
                                    color: "#fbbf24" }}>
                      ⚠ BIM stale
                    </span>
                  )}
                </td>
                <td style={td}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: meta.color }}>
                    <Icon size={12} /> {meta.label}
                  </span>
                </td>
                <td style={td} data-testid={`project-row-industry-${p.id}`}
                     title={p.sub_type || p.industry || ""}>
                  {getProjectIndustryLabel(p)}
                </td>
                <td style={td}>{p.country || "—"}</td>
                <td style={{ ...td, color: "var(--pf-text-muted)" }}>
                  {new Date(p.updated_at).toLocaleDateString()}
                </td>
                <td style={{ ...td, textAlign: "right" }}>
                  <Link to={`/projects/${p.id}`} className="btn btn-ghost"
                         data-testid={`project-open-${p.id}`}
                         style={{ marginRight: 4 }}>Open <ArrowUpRight size={12}/></Link>
                  <button className="btn btn-danger" onClick={() => onRemove(p.id)}
                           data-testid={`project-delete-${p.id}`}><Trash2 size={12}/></button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ───────────────────────── Empty state ───────────────────────────── */
function EmptyState({ onNew }) {
  return (
    <div className="pf-card pf-blueprint-bg" data-testid="dashboard-empty"
          style={{ padding: 60, textAlign: "center", position: "relative" }}>
      <Folder size={32} style={{ color: "var(--pf-accent)", marginBottom: 12 }} />
      <div className="pf-overline" style={{ marginBottom: 6 }}>NO PROJECTS YET</div>
      <h3 className="pf-heading" style={{ fontSize: 22, margin: "4px 0 8px" }}>
        Start with your first prompt
      </h3>
      <p style={{ color: "var(--pf-text-muted)", maxWidth: 460, margin: "0 auto 18px" }}>
        Pick a domain, an industry sub-sector, drop in your standards — Capexara Process Industry will draft engineering deliverables stage by stage.
      </p>
      <button className="btn btn-primary" onClick={onNew}
               data-testid="dashboard-empty-new"><Plus size={14}/> Create a project</button>
    </div>
  );
}

/* ─────────────────────────── helpers ─────────────────────────────── */
function ViewBtn({ active, onClick, children, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      style={{
        padding: "6px 10px",
        background: active ? "var(--pf-ember)" : "var(--pf-surface-2)",
        color: active ? "#fff" : "var(--pf-fg)",
        border: "none", cursor: "pointer", display: "flex",
        alignItems: "center",
      }}>{children}</button>
  );
}

const selStyle = {
  padding: "7px 8px", fontSize: 13, borderRadius: 6,
  background: "var(--pf-surface-2)", color: "var(--pf-fg)",
  border: "1px solid var(--pf-border)",
};
const th = {
  padding: "10px 14px", textAlign: "left",
  color: "var(--pf-text-muted)", fontWeight: 600, fontSize: 11,
  letterSpacing: "0.06em", textTransform: "uppercase",
};
const td = { padding: "12px 14px", color: "var(--pf-fg)" };
